/**
 * Utilitário para tratar erros de importação de planilha.
 * Converte erros HTTP 422 em objetos estruturados para facilitar o tratamento no frontend.
 */

import { IMPORT_ERROR_TYPES } from './importErrorConstants';

// Re-export constants for backward compatibility
export { IMPORT_ERROR_TYPES };

/**
 * Extrai o tipo de erro e os detalhes da resposta HTTP
 * @param {Object} error - Objeto de erro do axios
 * @returns {Object} - { type, message, details }
 */

export const parseImportError = (error) => {
  if (!error?.response) {
    return {
      type: IMPORT_ERROR_TYPES.UNKNOWN,
      message: 'Erro desconhecido ao importar planilha',
      details: null,
    };
  }

  const { status, data } = error.response;

  // Erros 422 são erros de validação estruturados
  if (status === 422 && data?.details) {
    const { details } = data;
    console.log('🚀 ~ parseImportError ~ details:', details);

    // Verifica tipo específico pelo campo 'type' ou pela estrutura dos dados
    if (details.type === 'missingColumns') {
      return {
        type: IMPORT_ERROR_TYPES.MISSING_COLUMNS,
        message: data.message || 'Algumas colunas obrigatórias não foram encontradas na planilha',
        details: {
          missingColumns: details.missingColumns || [],
          formattedMessage: details.message || details.missingColumns?.join('|') || '',
        },
      };
    }

    if (details.type === 'hiringModelMismatch') {
      return {
        type: IMPORT_ERROR_TYPES.HIRING_MODEL_MISMATCH,
        message: data.message || 'Há divergências no modelo de contratação',
        details: {
          mismatches: details.mismatches || [],
          formattedMessage: details.formattedMessage || '',
        },
      };
    }

    // Duplicidade de cooperados
    if (details.duplicates) {
      return {
        type: IMPORT_ERROR_TYPES.DUPLICATE_COOPERATORS,
        message: data.message || 'Há colaboradores duplicados na planilha',
        details: {
          duplicates: details.duplicates || [],
        },
      };
    }

    // Divergência entre planilha e tela
    if (details.inSheetNotOnScreen || details.inScreenNotInSheet) {
      return {
        type: IMPORT_ERROR_TYPES.SHEET_SCREEN_MISMATCH,
        message: data.message || 'Há divergências entre colaboradores na planilha e na tela',
        details: {
          inSheetNotOnScreen: details.inSheetNotOnScreen || [],
          inScreenNotInSheet: details.inScreenNotInSheet || [],
        },
      };
    }
  }

  // Erro de ajuda de custo (mensagem simples)
  if (status === 422 && data?.message?.includes('Ajuda de Custo')) {
    return {
      type: IMPORT_ERROR_TYPES.COST_ALLOWANCE_PRESENT,
      message: data.message || 'Impossível utilizar verba "Ajuda de Custo"',
      details: null,
    };
  }

  // Nenhum cooperador na planilha
  if (status === 422 && data?.message?.includes('Nenhum colaborador')) {
    return {
      type: IMPORT_ERROR_TYPES.NO_COOPERATORS,
      message: data.message || 'Nenhum colaborador encontrado na planilha',
      details: null,
    };
  }

  // Erro desconhecido
  return {
    type: IMPORT_ERROR_TYPES.UNKNOWN,
    message: data?.message || 'Erro ao importar planilha',
    details: data?.details || null,
  };
};
